/**
 * The app-icon badge — the number on FieldIQ's icon on a phone's home screen.
 *
 * It shows the bell's unread count (fieldiq-api lib/unread-notifications.ts):
 *   - public/sw.js sets it from `unread_count` on every push, so it moves while
 *     the app is closed;
 *   - PushInit syncs it to GET /notifications/unread-count whenever the app is
 *     open (on open, on focus, every 30 s with the bell), so reading
 *     notifications brings it down;
 *   - 0 clears it, and signing out clears it.
 *
 * Feature-detected: the Badging API exists on Android Chrome (installed app),
 * iOS 16.4+ home-screen apps and desktop Chromium. Elsewhere this is a no-op.
 */
export function appBadgeSupported(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

/** Set the badge to `count`; 0 (or less) clears it. Never throws. */
export async function setAppBadgeCount(count: number): Promise<void> {
  if (!appBadgeSupported() || !Number.isFinite(count)) return;
  const nav = navigator as Navigator & {
    setAppBadge: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0) await nav.setAppBadge(Math.floor(count));
    else if (nav.clearAppBadge) await nav.clearAppBadge();
    else await nav.setAppBadge(0);
  } catch {
    // A refused badge (permission not granted, unsupported surface) is not an error.
  }
}
